import { Fragment } from 'react';
import { ActionButton, EmptyState, ExportDropdown, SectionCard } from '@practium/ui';
import { STATE_SECTION_LABELS } from '../../constants/parameters.constants';
import type { ReferenceSectionProps, StateReferenceDTO, StateSectionKey } from '../../types/parameters.types';
import { exportParametersToExcel, exportParametersToPdf } from '../../utils/parametersExport';
import { LimingSection } from '../Sections/LimingSection';
import { PlantingP2O5Section } from '../Sections/PlantingP2O5Section';
import { PlasteringSection } from '../Sections/PlasteringSection';
import { RangesSection } from '../Sections/RangesSection';
import { SoilSection } from '../Sections/SoilSection';

const SECTION_KEYS = Object.keys(STATE_SECTION_LABELS) as StateSectionKey[];

interface StateReferenceCardProps {
	reference: StateReferenceDTO | null;
	section: StateSectionKey;
	isLoading: boolean;
	isError: boolean;
	isSaving: boolean;
	onSectionChange: (section: StateSectionKey) => void;
	onNewRegion: () => void;
	onSave: ReferenceSectionProps['onSave'];
	onConfirmDelete: ReferenceSectionProps['onConfirmDelete'];
}

const renderSection = (section: StateSectionKey, props: ReferenceSectionProps) => {
	if (section === 'macro' || section === 'micro' || section === 'indexes') {
		return <SoilSection key={section} group={section} {...props} />;
	}
	if (section === 'liming') return <LimingSection {...props} />;
	if (section === 'plastering') return <PlasteringSection {...props} />;
	if (section === 'p2o5') return <PlantingP2O5Section {...props} />;
	return <RangesSection key={section} kind={section} {...props} />;
};

export const StateReferenceCard = ({
	reference,
	section,
	isLoading,
	isError,
	isSaving,
	onSectionChange,
	onNewRegion,
	onSave,
	onConfirmDelete,
}: StateReferenceCardProps) => {
	const renderContent = () => {
		if (isError) return <EmptyState title="Erro ao carregar as regiões" description="Tente novamente em instantes." />;
		if (!reference) {
			return <EmptyState title="Nenhuma região cadastrada" description="Crie uma região para cadastrar os parâmetros." />;
		}
		return (
			<Fragment key={reference._id}>{renderSection(section, { reference, isSaving, onSave, onConfirmDelete })}</Fragment>
		);
	};

	return (
		<SectionCard
			title={reference?.referenceCode ?? 'Regiões'}
			subtitle={reference?.description || 'Parâmetros de interpretação e recomendação da região'}
			tabs={SECTION_KEYS.map((key) => ({
				key,
				label: STATE_SECTION_LABELS[key],
				content: key === section ? renderContent() : null,
			}))}
			activeTab={section}
			onTabChange={onSectionChange}
			isLoading={isLoading}
			headerAction={
				<>
					<ActionButton text="Nova região" onClick={onNewRegion} />
					<ExportDropdown
						disabled={!reference}
						onExportExcel={() => reference && exportParametersToExcel(reference)}
						onExportPdf={() => reference && exportParametersToPdf(reference)}
					/>
				</>
			}
		/>
	);
};
